import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SuperAdminLayout from './SuperAdminLayout';
import GuideButton from '../common/guide/GuideButton';
import SuperAdminService, { Company, DealerRow } from '../../services/SuperAdminService';

// ============================================================================
// BAYİLER YÖNETİM SAYFASI (yalnız süper admin — App.tsx RequireRole + backend
// DealerManagementController [SuperAdminOnly]).
//  - Bayi listesi: ad, e-posta, şirket sayısı, aktif/pasif toggle
//  - Bayi oluşturma diyaloğu (bayi + role='dealer' kullanıcısı tek adımda)
//  - Bayi detayı: atanmış şirketler + şirket ata/kaldır (arama seçicili)
// ============================================================================

const EMPTY_DEALER_FORM = {
  name: '',
  email: '',
  password: '',
  phone: '',
  firstName: '',
  lastName: '',
  /** Şirket açma kotası; metin olarak tutulur (boş bırakılabilsin diye). */
  maxCompanies: '0',
};

const SuperAdminDealers: React.FC = () => {
  const [dealers, setDealers] = useState<DealerRow[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'success', message: '' });

  // Bayi oluşturma diyaloğu
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_DEALER_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Bayi detay diyaloğu (şirket ata/kaldır)
  const [detailDealer, setDetailDealer] = useState<DealerRow | null>(null);
  const [companyToAssign, setCompanyToAssign] = useState<Company | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [unassigningCompanyId, setUnassigningCompanyId] = useState<string | null>(null);

  // Aktif/pasif toggle işlemi süren bayi
  const [togglingDealerId, setTogglingDealerId] = useState<number | null>(null);

  // Detay diyaloğundaki kota düzenleme
  const [quotaInput, setQuotaInput] = useState('0');
  const [savingQuota, setSavingQuota] = useState(false);

  const service = SuperAdminService.getInstance();

  const loadData = useCallback(async (showSpinner: boolean = false) => {
    try {
      if (showSpinner) setLoading(true);
      const [dealerList, companyList] = await Promise.all([
        service.getDealers(),
        service.getAllCompanies(),
      ]);
      setDealers(dealerList);
      setCompanies(companyList);
    } catch (error: any) {
      console.error('❌ [Bayiler] Veriler yüklenirken hata:', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Bayiler yüklenirken bir hata oluştu.',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData(true);
  }, [loadData]);

  const filteredDealers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return dealers;
    return dealers.filter(
      (d) =>
        d.name.toLowerCase().includes(term) ||
        (d.email || '').toLowerCase().includes(term) ||
        (d.phone || '').toLowerCase().includes(term)
    );
  }, [dealers, searchTerm]);

  /** Detay diyaloğundaki bayiye atanmış şirketler (companies.dealerId üzerinden). */
  const assignedCompanies = useMemo(() => {
    if (!detailDealer) return [];
    return companies.filter((c) => c.dealerId === detailDealer.id);
  }, [companies, detailDealer]);

  /** Bu bayiye atanabilir şirketler (zaten bu bayide olanlar hariç). */
  const assignableCompanies = useMemo(() => {
    if (!detailDealer) return [];
    return companies.filter((c) => c.dealerId !== detailDealer.id);
  }, [companies, detailDealer]);

  // ── Bayi oluşturma ──────────────────────────────────────────────────────

  const setCreateField = (field: keyof typeof EMPTY_DEALER_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setCreateForm((prev) => ({ ...prev, [field]: e.target.value }));

  /** Kota metnini sayıya çevirir; geçersiz/negatif değer null döner. */
  const parseQuota = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  const isCreateValid =
    createForm.name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(createForm.email.trim()) &&
    createForm.password.length >= 6 &&
    parseQuota(createForm.maxCompanies) !== null;

  const closeCreateDialog = () => {
    if (creating) return;
    setIsCreateOpen(false);
    setCreateForm(EMPTY_DEALER_FORM);
    setCreateError(null);
    setShowPassword(false);
  };

  const handleCreateDealer = async () => {
    if (!isCreateValid || creating) return;
    try {
      setCreating(true);
      setCreateError(null);
      await service.createDealer({
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        password: createForm.password,
        phone: createForm.phone.trim() || undefined,
        firstName: createForm.firstName.trim() || undefined,
        lastName: createForm.lastName.trim() || undefined,
        maxCompanies: parseQuota(createForm.maxCompanies) ?? 0,
      });
      setIsCreateOpen(false);
      setCreateForm(EMPTY_DEALER_FORM);
      setShowPassword(false);
      setSnackbar({ open: true, severity: 'success', message: 'Bayi başarıyla oluşturuldu.' });
      await loadData();
    } catch (error: any) {
      setCreateError(error?.message || 'Bayi oluşturulurken bir hata oluştu.');
    } finally {
      setCreating(false);
    }
  };

  // ── Aktif/pasif toggle ──────────────────────────────────────────────────

  const handleToggleDealer = async (dealer: DealerRow) => {
    if (togglingDealerId !== null) return;
    try {
      setTogglingDealerId(dealer.id);
      const isActive = await service.toggleDealer(dealer.id);
      setDealers((prev) => prev.map((d) => (d.id === dealer.id ? { ...d, isActive } : d)));
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${dealer.name} ${isActive ? 'aktifleştirildi' : 'pasifleştirildi'}.`,
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Bayi durumu güncellenemedi.',
      });
    } finally {
      setTogglingDealerId(null);
    }
  };

  // ── Şirket açma kotası ──────────────────────────────────────────────────

  /**
   * Bayinin şirket açma kotasını kaydeder. Kota yalnız bayinin KENDİ açtığı
   * şirketleri sınırlar; süper adminin elle atadıkları kotadan bağımsızdır,
   * bu yüzden kota mevcut şirket sayısının altına da çekilebilir.
   */
  const handleSaveQuota = async () => {
    if (!detailDealer || savingQuota) return;
    const parsed = parseQuota(quotaInput);
    if (parsed === null) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Şirket kotası 0 veya daha büyük bir tam sayı olmalıdır.',
      });
      return;
    }
    try {
      setSavingQuota(true);
      const result = await service.updateDealerMaxCompanies(detailDealer.id, parsed);
      setDealers((prev) =>
        prev.map((d) =>
          d.id === detailDealer.id
            ? { ...d, maxCompanies: result.maxCompanies, remainingQuota: result.remainingQuota }
            : d
        )
      );
      setDetailDealer((prev) =>
        prev ? { ...prev, maxCompanies: result.maxCompanies, remainingQuota: result.remainingQuota } : prev
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Kota güncellendi: ${result.companyCount}/${result.maxCompanies} şirket.`,
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Şirket kotası güncellenemedi.',
      });
    } finally {
      setSavingQuota(false);
    }
  };

  // ── Şirket ata / kaldır ─────────────────────────────────────────────────

  const handleAssignCompany = async () => {
    if (!detailDealer || !companyToAssign || assigning) return;
    try {
      setAssigning(true);
      await service.assignCompanyToDealer(detailDealer.id, companyToAssign.id);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${companyToAssign.name} → ${detailDealer.name} ataması yapıldı.`,
      });
      setCompanyToAssign(null);
      await loadData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Şirket atanırken bir hata oluştu.',
      });
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignCompany = async (company: Company) => {
    if (!detailDealer || unassigningCompanyId) return;
    try {
      setUnassigningCompanyId(company.id);
      await service.unassignCompanyFromDealer(detailDealer.id, company.id);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${company.name} ataması kaldırıldı.`,
      });
      await loadData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Şirket ataması kaldırılırken bir hata oluştu.',
      });
    } finally {
      setUnassigningCompanyId(null);
    }
  };

  return (
    <SuperAdminLayout title="Bayiler" icon={<StorefrontIcon />}>
      {/* Üst bar: arama + toplam + oluşturma */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Bayi ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              maxWidth: 400,
              '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
            }}
          />
          <Chip
            icon={<StorefrontIcon />}
            label={`Toplam Bayi: ${filteredDealers.length}`}
            color="primary"
          />
          <GuideButton guideKey="superadmin.dealers" />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Bayi Oluştur
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredDealers.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
          <StorefrontIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
          <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
            {searchTerm ? 'Arama Sonucu Bulunamadı' : 'Henüz Bayi Yok'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {searchTerm
              ? 'Arama kriterlerinize uygun bayi bulunamadı.'
              : '"Bayi Oluştur" ile ilk bayinizi ekleyin; ardından şirketleri bayiye atayabilirsiniz.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Bayi</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Bayi Kullanıcısı</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Telefon</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }} align="center">Şirket / Kota</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Kayıt Tarihi</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }} align="center">Aktif</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }} align="center">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDealers.map((dealer) => (
                <TableRow key={dealer.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: dealer.isActive ? '#1976d2' : '#9e9e9e', width: 40, height: 40 }}>
                        <StorefrontIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {dealer.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {dealer.email || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {dealer.users.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    ) : (
                      dealer.users.map((u) => (
                        <Typography key={u.id} variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {u.name || u.email}
                          {!u.isActive && (
                            <Chip label="pasif" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                          )}
                        </Typography>
                      ))
                    )}
                  </TableCell>
                  <TableCell>{dealer.phone || '-'}</TableCell>
                  <TableCell align="center">
                    {/* Kullanılan / kota. Kota dolduğunda bayi yeni şirket açamaz (403). */}
                    <Tooltip
                      title={
                        dealer.remainingQuota > 0
                          ? `Bayi ${dealer.remainingQuota} şirket daha açabilir`
                          : 'Kota dolu — bayi yeni şirket açamaz'
                      }
                    >
                      <Chip
                        label={`${dealer.companyCount} / ${dealer.maxCompanies ?? 0}`}
                        size="small"
                        color={dealer.remainingQuota > 0 ? 'primary' : 'error'}
                        variant={dealer.companyCount > 0 ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {dealer.createdAt ? new Date(dealer.createdAt).toLocaleDateString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={dealer.isActive ? 'Pasifleştir (girişleri engellenir)' : 'Aktifleştir'}>
                      <span>
                        <Switch
                          checked={dealer.isActive}
                          onChange={() => handleToggleDealer(dealer)}
                          disabled={togglingDealerId === dealer.id}
                          color="success"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<BusinessIcon />}
                      onClick={() => {
                        setDetailDealer(dealer);
                        setCompanyToAssign(null);
                        setQuotaInput(String(dealer.maxCompanies ?? 0));
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Şirketler
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Bayi Oluşturma Diyaloğu ─────────────────────────────────────── */}
      <Dialog open={isCreateOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontIcon />
            <Typography variant="h6" component="span">Yeni Bayi Oluştur</Typography>
          </Box>
          <IconButton size="small" onClick={closeCreateDialog} sx={{ color: 'white' }} disabled={creating}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Bayi Adı"
            value={createForm.name}
            onChange={setCreateField('name')}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            fullWidth
            label="E-posta (bayi girişi bu adresle yapılır)"
            type="email"
            value={createForm.email}
            onChange={setCreateField('email')}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Şifre"
            type={showPassword ? 'text' : 'password'}
            value={createForm.password}
            onChange={setCreateField('password')}
            helperText="En az 6 karakter"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword((p) => !p)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Telefon (opsiyonel)"
            value={createForm.phone}
            onChange={setCreateField('phone')}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Açabileceği Şirket Sayısı (kota)"
            type="number"
            value={createForm.maxCompanies}
            onChange={setCreateField('maxCompanies')}
            error={parseQuota(createForm.maxCompanies) === null}
            helperText="0 = bayi hiç şirket açamaz. Bayinin açtığı şirketlerde tüm modüller kapalı gelir; izni siz verirsiniz."
            inputProps={{ min: 0, step: 1 }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Yetkili Adı (opsiyonel)"
              value={createForm.firstName}
              onChange={setCreateField('firstName')}
            />
            <TextField
              fullWidth
              label="Yetkili Soyadı (opsiyonel)"
              value={createForm.lastName}
              onChange={setCreateField('lastName')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreateDialog} disabled={creating}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleCreateDealer}
            disabled={!isCreateValid || creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {creating ? 'Oluşturuluyor...' : 'Bayi Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bayi Detayı: Atanmış Şirketler + Ata/Kaldır ─────────────────── */}
      <Dialog
        open={detailDealer !== null}
        onClose={() => setDetailDealer(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <BusinessIcon />
            <Typography variant="h6" component="span" noWrap>
              {detailDealer?.name} — Şirketler
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDetailDealer(null)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {/* ── Şirket açma kotası ──────────────────────────────────────── */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Şirket Açma Kotası
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                type="number"
                label="Kota"
                value={quotaInput}
                onChange={(e) => setQuotaInput(e.target.value)}
                error={parseQuota(quotaInput) === null}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 120 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveQuota}
                disabled={
                  savingQuota ||
                  parseQuota(quotaInput) === null ||
                  parseQuota(quotaInput) === (detailDealer?.maxCompanies ?? 0)
                }
                startIcon={savingQuota ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.1 }}
              >
                {savingQuota ? 'Kaydediliyor...' : 'Kotayı Kaydet'}
              </Button>
              <Chip
                size="small"
                label={`${detailDealer?.companyCount ?? 0} / ${detailDealer?.maxCompanies ?? 0} kullanıldı`}
                color={(detailDealer?.remainingQuota ?? 0) > 0 ? 'primary' : 'error'}
                sx={{ mt: 1 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Kota yalnızca bayinin kendi açtığı şirketleri sınırlar; aşağıdan elle
              atadığınız şirketler kotayı tüketmez.
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Şirket atama seçicisi */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
            <Autocomplete
              fullWidth
              size="small"
              options={assignableCompanies}
              getOptionLabel={(c) => c.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={companyToAssign}
              onChange={(_e, value) => setCompanyToAssign(value)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.name}</Typography>
                    {option.dealerName && (
                      <Typography variant="caption" color="warning.main">
                        Şu an: {option.dealerName} (atama taşınır)
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Şirket ara ve seç..." placeholder="Şirket adı yazın" />
              )}
            />
            <Button
              variant="contained"
              onClick={handleAssignCompany}
              disabled={!companyToAssign || assigning}
              startIcon={assigning ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.1 }}
            >
              Ata
            </Button>
          </Box>

          <Divider sx={{ mb: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Atanmış Şirketler ({assignedCompanies.length})
          </Typography>

          {assignedCompanies.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Bu bayiye henüz şirket atanmamış.
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {assignedCompanies.map((company) => (
                <ListItem
                  key={company.id}
                  secondaryAction={
                    <Tooltip title="Atamayı kaldır (şirket merkeze döner)">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => handleUnassignCompany(company)}
                          disabled={unassigningCompanyId === company.id}
                        >
                          {unassigningCompanyId === company.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <LinkOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={company.logo} sx={{ width: 32, height: 32, bgcolor: '#1976d2', fontSize: '0.85rem' }}>
                      {company.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={company.name}
                    secondary={company.admin?.email}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailDealer(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SuperAdminLayout>
  );
};

export default SuperAdminDealers;
